import React, { useMemo, useRef, useState } from "react";
import useOnlineStatus from "@/hooks/useOnlineStatus";
import useVisitUploads from "@/hooks/useVisitUploads";
import useVisitForm from "@/hooks/useVisitForm";
import { STEPS, allIssues, stepIssues } from "@/lib/visitSchema";
import { Stepper, RequirementsChecklist } from "@/components/visit/VisitBits";
import BasicDetailsStep from "@/components/visit/steps/BasicDetailsStep";
import NeighborStep from "@/components/visit/steps/NeighborStep";
import LandRateStep from "@/components/visit/steps/LandRateStep";
import KhasraStep from "@/components/visit/steps/KhasraStep";
import LoanLeadsStep from "@/components/visit/steps/LoanLeadsStep";
import PropertyPhotosStep from "@/components/visit/steps/PropertyPhotosStep";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowRight, Loader2, RefreshCw, Send, WifiOff } from "lucide-react";

const PANELS = { basic: BasicDetailsStep, neighbor: NeighborStep, land_rate: LandRateStep, khasra: KhasraStep, leads: LoanLeadsStep, photos: PropertyPhotosStep };

function NetworkBadge({ online }) {
  return (
    <div className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${online ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`} data-testid="visit-network-status">
      {online ? <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> : <WifiOff className="h-3 w-3" />}
      {online ? "Online" : "Offline — saved locally"}
    </div>
  );
}

function PendingStrip({ uploads, online }) {
  if (!uploads.pending.length) return null;
  return (
    <div className="mb-4 flex items-center justify-between gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="visit-pending-strip">
      <span>{uploads.pending.length} photo(s) waiting to sync.</span>
      <Button type="button" size="sm" variant="outline" onClick={uploads.syncPending} disabled={uploads.syncing || !online} data-testid="visit-sync-button">
        {uploads.syncing ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <RefreshCw className="h-3 w-3 mr-1" />} Sync now
      </Button>
    </div>
  );
}

function StepNav({ step, setStep, last, canSubmit, busy, onSubmit }) {
  return (
    <div className="mt-5 flex items-center justify-between gap-2">
      <Button type="button" variant="outline" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0} data-testid="visit-prev"><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>
      {last ? (
        <Button type="button" onClick={onSubmit} disabled={busy || !canSubmit} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="visit-submit-button">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-1" /> Submit inspection</>}
        </Button>
      ) : (
        <Button type="button" onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))} className="bg-slate-950 hover:bg-slate-800" data-testid="visit-next">Next <ArrowRight className="h-4 w-4 ml-1" /></Button>
      )}
    </div>
  );
}

/* Six-step structured inspection: drafts persist to IndexedDB, photos queue offline, submit gated on requirements. */
export default function VisitWorkflow({ lead, onSaved, onCancel }) {
  const online = useOnlineStatus();
  const [step, setStep] = useState(0);
  const uploadsRef = useRef(null);
  const { form, setForm, busy, submit } = useVisitForm({ lead, online, uploadsRef, onSaved });
  const uploads = useVisitUploads({ leadId: lead.id, online, setForm });
  uploadsRef.current = uploads;

  const statuses = useMemo(() => Object.fromEntries(STEPS.map((s) => [s.key, stepIssues(form, s.key)])), [form]);
  const issues = useMemo(() => allIssues(form), [form]);
  const last = step === STEPS.length - 1;
  const Panel = PANELS[STEPS[step].key];

  return (
    <div className="mt-6 border-t border-slate-200 pt-4" data-testid="visit-workflow">
      <div className="flex items-center justify-between mb-3 gap-2">
        <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Property inspection · Step {step + 1} of {STEPS.length}</div>
        <div className="flex items-center gap-2">
          <NetworkBadge online={online} />
          {onCancel && <Button type="button" variant="ghost" size="sm" onClick={onCancel} data-testid="visit-cancel">Cancel</Button>}
        </div>
      </div>
      <PendingStrip uploads={uploads} online={online} />
      <Stepper steps={STEPS} current={step} statuses={statuses} onSelect={setStep} />
      <h3 className="font-heading text-lg font-semibold text-slate-950 mb-3" data-testid="visit-step-title">{STEPS[step].label}</h3>
      <Panel form={form} setForm={setForm} uploads={uploads} />
      {last && <div className="mt-5"><RequirementsChecklist issues={issues} /></div>}
      <StepNav step={step} setStep={setStep} last={last} canSubmit={online && issues.length === 0} busy={busy} onSubmit={submit} />
    </div>
  );
}
